import React, { useState } from "react";
import Modal from "../utils/Modal/Modal";
import { ReactComponent as CloseIcon } from "../../assets/icons/close.svg";
import { ReactComponent as LockIcon } from "../../assets/icons/lock.svg";
import { ReactComponent as StarIcon } from "../../assets/icons/star.svg";

import {
  ModalContainer,
  PromptCancelButton,
  PromptCloseButton,
  PromptInputContainer,
  PromptInputTitle,
  PromptModalButtons,
  PromptModalHeader,
  PromptModalTitle,
  PromptSeedInput,
  PromptSubmitButton,
  PromptTextarea,
  VideoQualityContainer,
  VideoQualityTitle,
  FpsContainer,
  FpsTitle,
  DurationContainer,
  DurationTitle,
  Buttons,
  Button,
  PromptRow,
  Badge,
  RelativeContainer,
  EstTimeText,
  EstTimeContainer,
  SummaryContainer,
} from "./EnterPromptModal.styles";
import {
  ALL_VIDEO_RESOLUTIONS,
  DEFAULT_VIDEO_DURATION,
  DEFAULT_VIDEO_FPS,
  DEFAULT_VIDEO_RESOLUTION,
  PRO_VIDEO_DURATION,
  VIDEO_RESOLUTION,
  VIDEO_DURATION_PRICE,
  VIDEO_RESOLUTION_PRICE,
  VIDEO_FPS_PRICE,
} from "../../utils/constants";
import { useSelector } from "react-redux";
import { selectUser } from "../../store/slices/user/slice";
import UnlockModal from "../UnlockModal/UnlockModal";

const VIDEO_RESOLUTION_DESCRIPTION = {
  LOW: "2 mins",
  MEDIUM: "5 mins",
  HIGH: "15 mins",
};

const FPS_OPTIONS = [30, 45, 60];
const DURATION_OPTIONS = [5, 7, 10, 12];

const BASIC_PLAN_NAME = "Basic";
const PRO_PLAN_NAME = "Pro";
const PREMIUM_PLAN_NAME = "Premium";

const EnterPromptModal = ({
  isOpen,
  isRegenerate,
  onClose,
  customPrompt,
  setCustomPrompt,
  videoResolution,
  setVideoResolution,
  setEditedResolution,
  collectionVideoResolution,
  enableVideoQuality,
  customSeed,
  setCustomSeed,
  fps,
  collectionSeed,
  setFps,
  setEditedVideoFps,
  collectionVideoFps,
  videoDuration,
  setVideoDuration,
  collectionVideoDuration,
  setEditedVideoDuration,
  modelPrice,
}) => {
  const user = useSelector(selectUser);
  const [openUnlockModal, setOpenUnlockModal] = useState(false);
  const [seedMode, setSeedMode] = React.useState(() => {
    return localStorage.getItem("seedMode") || "random";
  });

  const isProEnabled =
    [PRO_PLAN_NAME, PREMIUM_PLAN_NAME].includes(user?.subscription?.name) &&
    user?.subscription?.endDate &&
    new Date(user?.subscription?.endDate) > new Date();

  const isBasicEnabled =
    user?.subscription?.name === BASIC_PLAN_NAME &&
    user?.subscription?.endDate &&
    new Date(user?.subscription?.endDate) > new Date();

  const isSubscribed = isBasicEnabled || isProEnabled;

  const calculateTotalPrice = (modelPrice) => {
    let totalPrice = modelPrice;

    if (videoResolution && VIDEO_RESOLUTION_PRICE[videoResolution]) {
      totalPrice += VIDEO_RESOLUTION_PRICE[videoResolution];
    }

    if (videoDuration && VIDEO_DURATION_PRICE[videoDuration]) {
      totalPrice += VIDEO_DURATION_PRICE[videoDuration];
    }

    if (fps && VIDEO_FPS_PRICE[fps]) {
      totalPrice += VIDEO_FPS_PRICE[fps];
    }

    return totalPrice;
  };

  const totalPrice = calculateTotalPrice(modelPrice);

  const generateRandomSeed = () => {
    return Math.floor(Math.random() * 10000000) + 1;
  };

  React.useEffect(() => {
    if (seedMode === "random") {
      setCustomSeed(generateRandomSeed());
    } else if (seedMode === "custom") {
      if (collectionSeed) {
        setCustomSeed(collectionSeed);
      } else {
        setCustomSeed(generateRandomSeed());
      }
    }
  }, [seedMode, setCustomSeed, collectionSeed]);

  React.useEffect(() => {
    const savedSeedMode = localStorage.getItem("seedMode");
    const savedCustomSeed = localStorage.getItem("customSeed");

    if (savedSeedMode) {
      setSeedMode(savedSeedMode);

      if (savedSeedMode === "custom" && savedCustomSeed) {
        setCustomSeed(Number(savedCustomSeed));
      }
    }
  }, [setCustomSeed]);

  React.useEffect(() => {
    if (!isRegenerate) {
      if (!isSubscribed) {
        setFps(30);
        localStorage.setItem("videoFps", 30);

        setVideoDuration(5);
        localStorage.setItem("videoDuration", 5);

        setVideoResolution(VIDEO_RESOLUTION.LOW);
        localStorage.setItem("videoResolution", VIDEO_RESOLUTION.LOW);
      } else {
        const maxAllowed = isProEnabled ? 60 : 45;
        const allowedFps = Math.min(fps || DEFAULT_VIDEO_FPS, maxAllowed);

        setFps(allowedFps);

        localStorage.setItem("videoFps", allowedFps);
        if (!isProEnabled) {
          if (videoResolution === VIDEO_RESOLUTION.HIGH) {
            setVideoResolution(VIDEO_RESOLUTION.MEDIUM);
            localStorage.setItem("videoResolution", VIDEO_RESOLUTION.MEDIUM);
          }
          if (PRO_VIDEO_DURATION.includes(videoDuration)) {
            setVideoDuration(DEFAULT_VIDEO_DURATION);
            localStorage.setItem("videoDuration", DEFAULT_VIDEO_DURATION);
          }
        }
      }
    }
  }, [
    fps,
    isSubscribed,
    isProEnabled,
    setVideoResolution,
    setVideoDuration,
    videoResolution,
    videoDuration,
    isRegenerate,
    collectionVideoFps,
    setFps,
    collectionVideoDuration,
    collectionVideoResolution,
  ]);

  const isDurationEnabled = (duration) => {
    if (!isSubscribed) return duration === 5;
    if (isBasicEnabled) return duration <= 7;
    if (isProEnabled) return true;
    return false;
  };

  const shouldShowDurationBadge = (duration) => {
    if (duration === 5) return null;
    if (duration === 7) {
      return !isSubscribed ? "basic" : null;
    }
    if (duration === 10 || duration === 12) {
      return !isProEnabled ? "pro" : null;
    }
    return null;
  };

  const isFpsEnabled = (fpsValue) => {
    if (fpsValue === 30) return true;
    if (fpsValue === 45) return isBasicEnabled || isProEnabled;
    if (fpsValue === 60 || fpsValue === 120) return isProEnabled;
    return false;
  };

  const shouldShowFpsBadge = (fpsValue) => {
    if (fpsValue === 30) return null;
    if (fpsValue === 45) {
      return !isSubscribed ? "basic" : null;
    }
    if (fpsValue === 60 || fpsValue === 120) {
      return !isProEnabled ? "pro" : null;
    }
    return null;
  };

  const handleFpsClick = (fpsValue) => {
    if (isFpsEnabled(fpsValue)) {
      setFps(fpsValue);
      localStorage.setItem("videoFps", fpsValue);
      setEditedVideoFps(
        fpsValue !== (Number(collectionVideoFps) ?? DEFAULT_VIDEO_FPS)
      );
    }
  };

  const handleDurationClick = (duration) => {
    if (isDurationEnabled(duration) && videoDuration !== duration) {
      setVideoDuration(duration);
      localStorage.setItem("videoDuration", duration);

      setEditedVideoDuration(
        duration !== (Number(collectionVideoDuration) ?? DEFAULT_VIDEO_DURATION)
      );
    }
  };

  const shouldShowVideoQualityBadge = (quality) => {
    if (quality === "LOW") return null;
    if (quality === "MEDIUM") {
      return !isSubscribed ? "basic" : null;
    }
    if (quality === "HIGH") {
      return !isProEnabled ? "pro" : null;
    }
    return null;
  };

  const isVideoQualityEnabled = (quality) => {
    if (quality === "LOW") return true;
    if (quality === "MEDIUM") return isBasicEnabled || isProEnabled;
    if (quality === "HIGH") return isProEnabled;
    return false;
  };

  const renderDurationOption = (duration) => {
    const isEnabled = isDurationEnabled(duration);
    const isSelected = videoDuration === duration;
    const badge = shouldShowDurationBadge(duration);

    return (
      <Button
        key={duration}
        $disabled={!isEnabled}
        $active={isSelected}
        onClick={() => {
          if (isEnabled) {
            handleDurationClick(duration);
          } else {
            setOpenUnlockModal(true);
          }
        }}
      >
        {duration}s
        {badge === "basic" && (
          <Badge>
            <LockIcon />
            Basic
          </Badge>
        )}
        {badge === "pro" && (
          <Badge $pro>
            <LockIcon />
            Pro
          </Badge>
        )}
      </Button>
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalContainer>
        <PromptModalHeader>
          <PromptModalTitle>Customize Scene</PromptModalTitle>
          <PromptCloseButton
            onClick={() => {
              onClose();
              setCustomPrompt("");
            }}
          >
            <CloseIcon />
          </PromptCloseButton>
        </PromptModalHeader>

        <PromptInputContainer>
          <PromptInputTitle>Custom Prompt</PromptInputTitle>
          <RelativeContainer
            onClick={() => {
              //   if (!isSubscribed) {
              //     setOpenUnlockModal(true);
              //   }
            }}
          >
            <PromptTextarea
              disabled={false}
              placeholder="Enter your custom prompt here..."
              value={customPrompt}
              onChange={(e) => setCustomPrompt(e.target.value)}
            />
            {/* {!isSubscribed && (
              <Badge>
                <LockIcon />
                Basic
              </Badge>
            )} */}
          </RelativeContainer>
        </PromptInputContainer>

        <PromptInputContainer>
          <PromptInputTitle>Seed</PromptInputTitle>
          <PromptRow>
            <Buttons>
              <Button
                $active={seedMode === "random"}
                onClick={() => {
                  setSeedMode("random");
                  localStorage.setItem("seedMode", "random");
                  setCustomSeed(generateRandomSeed());
                }}
              >
                Random
              </Button>
              <Button
                $active={seedMode === "custom"}
                onClick={() => {
                  setSeedMode("custom");
                  localStorage.setItem("seedMode", "custom");
                  localStorage.setItem("customSeed", collectionSeed);
                }}
              >
                Custom
              </Button>
            </Buttons>

            <PromptSeedInput
              disabled={seedMode === "random"}
              placeholder="Enter your seed here..."
              value={customSeed}
              min={1}
              max={9007199254740991}
              onChange={(e) => {
                const value = Number(e.target.value);
                if (value >= 0 && value <= 9007199254740991) {
                  setCustomSeed(value > 0 ? value : 1);
                  if (seedMode === "custom") {
                    localStorage.setItem("customSeed", value > 0 ? value : 1);
                  }
                }
              }}
            />
          </PromptRow>
        </PromptInputContainer>

        <VideoQualityContainer>
          {enableVideoQuality && (
            <>
              <VideoQualityTitle>Video Quality</VideoQualityTitle>
              <Buttons>
                {ALL_VIDEO_RESOLUTIONS.map((videoResolutionType) => {
                  const badge =
                    shouldShowVideoQualityBadge(videoResolutionType);
                  const isEnabled = isVideoQualityEnabled(videoResolutionType);

                  return (
                    <Button
                      onClick={() => {
                        if (isEnabled) {
                          setVideoResolution(videoResolutionType);
                          setEditedResolution(
                            videoResolutionType !==
                              (collectionVideoResolution ??
                                DEFAULT_VIDEO_RESOLUTION)
                          );
                          localStorage.setItem(
                            "videoResolution",
                            videoResolutionType
                          );
                        } else {
                          setOpenUnlockModal(true);
                        }
                      }}
                      $active={videoResolutionType === videoResolution}
                      key={videoResolutionType}
                      $disabled={!isEnabled}
                    >
                      {videoResolutionType.charAt(0).toUpperCase() +
                        videoResolutionType.slice(1).toLowerCase()}

                      {badge === "basic" && (
                        <Badge>
                          <LockIcon />
                          Basic
                        </Badge>
                      )}
                      {badge === "pro" && (
                        <Badge $pro>
                          <LockIcon />
                          Pro
                        </Badge>
                      )}
                    </Button>
                  );
                })}
              </Buttons>
            </>
          )}
        </VideoQualityContainer>

        {enableVideoQuality && (
          <FpsContainer>
            <FpsTitle>FPS</FpsTitle>

            <Buttons>
              {FPS_OPTIONS.map((fpsValue) => {
                const badge = shouldShowFpsBadge(fpsValue);
                const isEnabled = isFpsEnabled(fpsValue);

                return (
                  <Button
                    key={fpsValue}
                    $disabled={!isEnabled}
                    $active={fps === fpsValue}
                    onClick={() => {
                      if (isEnabled) {
                        handleFpsClick(fpsValue);
                      } else {
                        setOpenUnlockModal(true);
                      }
                    }}
                  >
                    {fpsValue} FPS
                    {badge === "basic" && (
                      <Badge>
                        <LockIcon />
                        Basic
                      </Badge>
                    )}
                    {badge === "pro" && (
                      <Badge $pro>
                        <LockIcon />
                        Pro
                      </Badge>
                    )}
                  </Button>
                );
              })}
            </Buttons>
          </FpsContainer>
        )}

        {enableVideoQuality && (
          <DurationContainer>
            <DurationTitle>Duration</DurationTitle>
            <Buttons>{DURATION_OPTIONS.map(renderDurationOption)}</Buttons>
          </DurationContainer>
        )}

        <SummaryContainer>
          <EstTimeContainer>
            <EstTimeText>Est Time:</EstTimeText>
            <EstTimeText $bold>
              {VIDEO_RESOLUTION_DESCRIPTION[videoResolution]}
            </EstTimeText>
          </EstTimeContainer>
          <EstTimeContainer>
            <EstTimeText>Cost:</EstTimeText>
            <StarIcon />
            <EstTimeText $bold>{totalPrice}</EstTimeText>
          </EstTimeContainer>
        </SummaryContainer>

        <PromptModalButtons>
          <PromptSubmitButton
            $type="primary"
            onClick={() => {
              onClose(false);
            }}
            disabled={false}
          >
            Save
          </PromptSubmitButton>

          <PromptCancelButton
            onClick={() => {
              onClose();
            }}
          >
            Cancel
          </PromptCancelButton>
        </PromptModalButtons>
      </ModalContainer>
      <UnlockModal
        isOpen={openUnlockModal}
        onClose={() => setOpenUnlockModal(false)}
      />
    </Modal>
  );
};

export default EnterPromptModal;
